import { useEffect, useRef, useState } from "react";
import type { AudioAssetDto, AudioVersionDto } from "../api/types";
import { anchored } from "../lib/anchor";
import { formatBytes, formatTime } from "../lib/format";
import { Icon } from "./icons";

/**
 * The report's version switcher ("This file"): which delivery of this file the
 * project uses, chosen without leaving the report.
 *
 * Shown only when the file has more than one version. Choosing one does what
 * the project page's version tag does - it becomes the version every report,
 * check, download and the Pickups Package follow - and, since each version
 * keeps the proof it was built on, the report opens that version's own. A
 * version not proofed yet has no report to open, so it is listed but can only
 * be chosen from the project page.
 */

// "ch1 (v2).wav" is our storage name for a same-named re-delivery: the file was
// delivered as "ch1.wav". A delivery that came in under another name keeps it.
const deliveredAs = (filename: string) => filename.replace(/ \(v\d+\)(\.[^.]+)$/, "$1");

export function VersionSwitch({ asset, viewing, busy, onSwitch }: {
  asset: AudioAssetDto;
  viewing: string | null;            // the chapter the report shows
  busy: boolean;
  onSwitch: (v: AudioVersionDto) => void;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const btn = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    if (!rect) return;
    const close = (e: KeyboardEvent) => { if (e.key === "Escape") setRect(null); };
    window.addEventListener("keydown", close);
    return () => window.removeEventListener("keydown", close);
  }, [rect]);

  const versions = asset.versions ?? [];
  if (versions.length < 2) return null;
  const inUse = versions.find((v) => v.is_current);
  const shown = versions.find((v) => v.chapter_id === viewing) ?? inUse;
  const stale = !!shown && !shown.is_current;           // a report of a version not in use
  const name = asset.display_name || asset.filename;

  return (
    <>
      <button
        type="button"
        ref={btn}
        className={`btn ghost sm vswitch${rect ? " on" : ""}${stale ? " stale" : ""}`}
        disabled={busy}
        aria-haspopup="menu"
        aria-expanded={!!rect}
        title={`This file has ${versions.length} versions — choose which one the project uses`}
        onClick={() => setRect(rect ? null : btn.current?.getBoundingClientRect() ?? null)}
      >
        Version {shown?.version ?? asset.version}
        {stale && <span className="vswitch-note">not in use</span>}
        <Icon name={rect ? "chevron-up" : "chevron-down"} size={13} />
      </button>
      {rect && (
        <>
          <div className="vtag-scrim" onClick={() => setRect(null)} />
          <div className="vtag-menu vswitch-menu" role="menu" aria-label={`Versions of ${name}`}
            style={anchored(rect, { width: 340, zIndex: 31 })}>
            <span className="vtag-head">Version in use</span>
            {versions.map((v) => {
              const delivered = deliveredAs(v.filename);
              const can = !v.is_current && !!v.proofed && !!v.chapter_id;
              return (
                <button
                  type="button"
                  role="menuitemradio"
                  aria-checked={v.is_current}
                  key={v.filename}
                  className={`vtag-item${v.is_current ? " on" : ""}`}
                  disabled={busy || !can}
                  title={v.is_current ? "The version the project uses"
                    : !v.proofed ? "Not proofed yet, so there's no report to open — switch to it from the project page"
                    : `Use version ${v.version} and open its report`}
                  onClick={() => { setRect(null); onSwitch(v); }}
                >
                  <span className="vtag-check">{v.is_current ? <Icon name="check" size={13} /> : null}</span>
                  <span className="vtag-body">
                    <span className="vtag-name">
                      Version {v.version}{delivered !== name && <> <em>{delivered}</em></>}
                    </span>
                    <span className="vtag-meta">
                      {new Date(v.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                      {v.duration_seconds ? ` · ${formatTime(v.duration_seconds)}` : ""}
                      {" · "}{formatBytes(v.size_bytes)}
                      {" · "}
                      {v.is_current ? "in use" : v.proofed ? "has its own report" : "not proofed yet"}
                      {v.chapter_id === viewing && !v.is_current ? " · showing" : ""}
                    </span>
                  </span>
                </button>
              );
            })}
            <span className="vtag-note">
              The version in use is the one the whole project follows: reports, checks,
              downloads and the Pickups Package. Each version keeps its own report, so
              switching back brings that one with it.
            </span>
          </div>
        </>
      )}
    </>
  );
}
